import { Grid, makeStyles } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/styles';
import { createBrowserHistory as createHistory } from 'history';
import { Helmet } from 'react-helmet';
import { theme } from 'components/layout/Layout';
import privacyPolicy from 'pages/privacyPolicy';
import React from 'react';
import { Redirect, Link, Route, Router, Switch } from 'react-router-dom';
import { ConfirmSignUp, CreateNewPassword } from 'pages/auth';
import { CANONICAL_ROOT_URL, hasDedicatedSeoMetadata } from 'components/common/SeoMetadata';

export const SIGN_IN_ROUTE = '/login';
export const SIGN_UP_ROUTE = '/signup';
export const FORGOT_PASSWORD_ROUTE = '/forgot-password';
export const CONFIRM_SIGN_UP_ROUTE = '/confirm-signup';
export const RESET_PASSWORD_ROUTE = '/reset-password';
export const PRIVACY_POLICY_ROUTE = '/privacy-policy';

export const PUBLIC_AUTH_ROUTES = [
  SIGN_IN_ROUTE,
  SIGN_UP_ROUTE,
  FORGOT_PASSWORD_ROUTE,
  CONFIRM_SIGN_UP_ROUTE,
  RESET_PASSWORD_ROUTE,
  PRIVACY_POLICY_ROUTE,
];

export const PUBLIC_AUTH_ENTRY_STATE_BY_ROUTE: Record<string, string> = {
  [SIGN_IN_ROUTE]: 'signIn',
  [SIGN_UP_ROUTE]: 'signUp',
  [FORGOT_PASSWORD_ROUTE]: 'forgotPassword',
};

export const authHistory = createHistory();

export const useStyles = makeStyles({
  form: {
    width: '100%',
  },
  imageContainer: {
    paddingBottom: theme.spacing(2.5),
  },
  title: {
    fontSize: '2vw',
    fontFamily: 'Lato, sans-serif',
    fontWeight: 400,
    lineHeight: '1.235',
  },
  signInButton: {
    marginTop: theme.spacing(4),
    textTransform: 'none',
  },
  signUp: {
    paddingTop: theme.spacing(4),
  },
  forgotPass: {
    paddingTop: theme.spacing(3),
  },
  root: {
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
    flexDirection: 'column',
  },
  minWidthOneThird: {
    minWidth: 'calc(100vw / 3)',
  },
  cardContent: {
    justifyContent: 'center',
    padding: theme.spacing(4),
    maxWidth: 600,
  },
  logo: {
    width: 90,
    margin: theme.spacing(4, 4, 8),
  },
  signinLink: {
    padding: theme.spacing(4, 0),
  },
  badgeLink: {
    textAlign: 'center',
    width: 288,
    height: 83,
  },
  badge: {
    width: '100%',
    height: 83,
  },
  badgesContainer: {
    display: 'flex',
    height: 120,
    justifyContent: 'space-between',
    width: 600,
    alignItems: 'center',
  },
  benefitsContainer: {
    minWidth: 490,
    [theme.breakpoints.down('xl')]: {
      display: 'block',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  pageWithBenefitsLayout: {
    flexWrap: 'nowrap',
  },
  countryIcon: {
    maxWidth: 22,
    maxHeight: 22,
    marginRight: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    width: '100%',
  },
});

const AuthLayout = ({ children, entryRoute }) => {
  const classes = useStyles();
  const currentPathname = window.location.pathname;
  const canonicalUrl =
    currentPathname === '/' ? CANONICAL_ROOT_URL : `${window.location.origin}${currentPathname}`;
  const shouldRenderCanonical = !hasDedicatedSeoMetadata(currentPathname);

  const entryScreenRoutes = entryRoute
    ? [
        <Route key="entry-screen" exact path={entryRoute}>
          {children}
        </Route>,
        <Route
          key="entry-fallback"
          render={({ location }) =>
            PUBLIC_AUTH_ROUTES.includes(location.pathname) ? null : <Redirect to={SIGN_IN_ROUTE} />
          }
        />,
      ]
    : [<Route key="auth-children">{children}</Route>];

  return (
    <>
      {shouldRenderCanonical && (
        <Helmet>
          <link rel="canonical" href={canonicalUrl} />
        </Helmet>
      )}
      <Router history={authHistory}>
        <ThemeProvider theme={theme}>
          <Grid container>
            <Switch>
              <Route exact path={PRIVACY_POLICY_ROUTE}>
                <Link className={classes.signinLink} to={SIGN_IN_ROUTE}>
                  Go to sign in
                </Link>
                {React.createElement(privacyPolicy.show)}
              </Route>
              <Route exact path={CONFIRM_SIGN_UP_ROUTE}>
                <ConfirmSignUp notLoggedIn />
              </Route>
              <Route exact path={RESET_PASSWORD_ROUTE}>
                <CreateNewPassword />
              </Route>
              {entryScreenRoutes}
            </Switch>
          </Grid>
        </ThemeProvider>
      </Router>
    </>
  );
};

export default AuthLayout;
